/**
 * Progressive enhancement only. Without this file the nav is a plain always-
 * visible list of links, the TOC is a list of anchors, and the blog index
 * paginates through its "Older posts" link.
 */

import {
  nextThemePreference,
  storedTheme,
  THEME_KEY,
  WORLDLINE_EVENT,
  WORLDLINE_KEY,
  type Theme,
  type ThemePreference,
} from './theme-state.ts'

// --- theme toggle ----------------------------------------------------------
const toggle = document.querySelector<HTMLButtonElement>('#theme-toggle')
const systemTheme = matchMedia('(prefers-color-scheme: dark)')
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)')
const system = (): Theme => (systemTheme.matches ? 'dark' : 'light')
let memoryUnlocked = false
let memoryPreference: ThemePreference | undefined

const readUnlocked = (): boolean => {
  if (memoryUnlocked) return true
  try { return localStorage.getItem(WORLDLINE_KEY) === '1' } catch { return false }
}

const read = (): ThemePreference => {
  if (memoryPreference !== undefined) return memoryPreference
  try { return storedTheme(localStorage.getItem(THEME_KEY), readUnlocked()) } catch { return null }
}

const label = (current: ThemePreference, unlocked: boolean): void => {
  const shown = document.documentElement.dataset.theme ?? system()
  const next = nextThemePreference(current, unlocked) ?? 'system'
  toggle?.setAttribute('aria-label', `Colour theme: ${shown}. Activate for ${next} theme`)
}

const apply = (theme: Theme, preference: ThemePreference, unlocked: boolean): void => {
  document.documentElement.dataset.theme = theme
  label(preference, unlocked)
}

label(read(), readUnlocked())

toggle?.addEventListener('click', () => {
  const unlocked = readUnlocked()
  const next = nextThemePreference(read(), unlocked)
  memoryPreference = next
  try {
    if (next === null) localStorage.removeItem(THEME_KEY)
    else localStorage.setItem(THEME_KEY, next)
  } catch {}
  apply(next ?? system(), next, unlocked)
})

systemTheme.addEventListener('change', () => {
  if (read() === null) apply(system(), null, readUnlocked())
})

window.addEventListener(WORLDLINE_EVENT, () => {
  memoryUnlocked = true
  memoryPreference = 'worldline'
  try {
    localStorage.setItem(WORLDLINE_KEY, '1')
    localStorage.setItem(THEME_KEY, 'worldline')
  } catch {}

  const commit = (): void => apply('worldline', 'worldline', true)
  if (reducedMotion.matches) {
    commit()
    return
  }
  document.documentElement.classList.add('worldline-shift')
  window.setTimeout(commit, 250)
  window.setTimeout(() => document.documentElement.classList.remove('worldline-shift'), 500)
})

// --- TOC scroll-spy -------------------------------------------------------
const tocLinks = [...document.querySelectorAll<HTMLAnchorElement>('.toc a')]
if (tocLinks.length > 0) {
  const byId = new Map(tocLinks.map((a) => [a.getAttribute('href')?.slice(1) ?? '', a]))
  const headings = [...byId.keys()]
    .map((id) => document.getElementById(id))
    .filter((el): el is HTMLElement => el !== null)

  const spy = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue
        for (const a of tocLinks) a.removeAttribute('aria-current')
        byId.get(entry.target.id)?.setAttribute('aria-current', 'true')
      }
    },
    { rootMargin: '-10% 0px -80% 0px' },
  )
  for (const h of headings) spy.observe(h)
}

// --- infinite scroll over the build-time paginated pages ------------------
const list = document.querySelector<HTMLElement>('#post-list')
const sentinel = document.querySelector<HTMLElement>('#sentinel')
const olderLink = document.querySelector<HTMLElement>('.older-posts')

if (list && sentinel) {
  let next: string | null = sentinel.dataset.next ?? null
  let loading = false

  // The link stays in the DOM. If a fetch fails we reveal it again rather
  // than leaving the reader at a dead end.
  if (olderLink) olderLink.style.display = 'none'

  const loader = new IntersectionObserver(
    async (entries) => {
      if (!entries[0]?.isIntersecting || loading || next === null) return
      loading = true
      const target = next

      try {
        const res = await fetch(target)
        if (!res.ok) throw new Error(String(res.status))
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html')

        const items = doc.querySelectorAll('#post-list > li')
        if (items.length === 0) throw new Error('no items')
        list.append(...items)

        next = doc.querySelector<HTMLElement>('#sentinel')?.dataset.next ?? null

        // Keep back and refresh landing where the reader actually is.
        history.replaceState(null, '', target)

        if (next === null) {
          loader.disconnect()
          sentinel.remove()
          if (olderLink) olderLink.remove()
        } else {
          sentinel.dataset.next = next
        }
      } catch {
        loader.disconnect()
        if (olderLink) olderLink.style.display = ''
        next = null
      } finally {
        loading = false
      }
    },
    { rootMargin: '400px' },
  )

  loader.observe(sentinel)
}

// --- copy buttons on code blocks -----------------------------------------
// Added by script rather than emitted at build time: a button that cannot work
// without JavaScript should not be in the markup at all.
for (const pre of document.querySelectorAll<HTMLPreElement>('pre')) {
  const code = pre.textContent ?? ''
  if (code.trim() === '') continue

  const button = document.createElement('button')
  button.type = 'button'
  button.className = 'copy-code'
  button.textContent = 'Copy'
  button.setAttribute('aria-label', 'Copy code to clipboard')

  button.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(code)
      button.textContent = 'Copied'
    } catch {
      // Clipboard is permission-gated and unavailable over plain http on some
      // browsers. Say so rather than silently appearing to work.
      button.textContent = 'Press Ctrl+C'
    }
    window.setTimeout(() => {
      button.textContent = 'Copy'
    }, 1600)
  })

  // The wrapper is what positions the button, so pre keeps its own scrolling.
  const wrap = document.createElement('div')
  wrap.className = 'code-wrap'
  pre.parentNode?.insertBefore(wrap, pre)
  wrap.append(pre, button)
}

// --- search --------------------------------------------------------------
// Fetches a small index on first keystroke, not on page load: most visitors
// never search, and this keeps the index off the critical path entirely.
const searchBox = document.querySelector<HTMLElement>('.search')
const searchInput = document.querySelector<HTMLInputElement>('#search-input')
const searchResults = document.querySelector<HTMLElement>('#search-results')
const searchCount = document.querySelector<HTMLElement>('#search-count')

interface IndexedPost {
  t: string
  u: string
  s: string
  g: string[]
  e: string | null
  d: string
}

if (searchBox && searchInput && searchResults && searchCount) {
  // Only now is the control usable, so only now does it appear.
  searchBox.hidden = false

  let index: IndexedPost[] | null = null
  let loading: Promise<void> | null = null

  const load = (): Promise<void> => {
    loading ??= fetch('/search.json')
      .then((r) => (r.ok ? r.json() : []))
      .then((data: IndexedPost[]) => {
        index = data
      })
      .catch(() => {
        index = []
      })
    return loading
  }

  /** Terms come from a text input, so they must not be read as a pattern. */
  const escapeRe = (v: string): string => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  /**
   * Relevance score, or 0 when any term is missing entirely — every term must
   * match somewhere, so "kernel filter" does not return posts about only one.
   *
   * A hit at the start of a word outranks one mid-word: "kern" matching
   * "kernels" is a stronger signal than matching "unkernelled". Title outranks
   * summary, which outranks tags and series name.
   */
  const score = (p: IndexedPost, terms: string[]): number => {
    const title = p.t.toLowerCase()
    const summary = p.s.toLowerCase()
    const rest = `${p.g.join(' ')} ${p.e ?? ''}`.toLowerCase()
    let total = 0
    for (const term of terms) {
      const boundary = new RegExp(`\\b${escapeRe(term)}`)
      let best = 0
      if (title.startsWith(term)) best = 10
      else if (boundary.test(title)) best = 8
      else if (title.includes(term)) best = 5
      else if (boundary.test(summary)) best = 3
      else if (summary.includes(term)) best = 2
      else if (rest.includes(term)) best = 1
      // A term matching nothing disqualifies the record outright.
      if (best === 0) return 0
      total += best
    }
    return total
  }

  const esc = (v: string): string =>
    v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

  const render = (hits: IndexedPost[]): void => {
    searchResults.innerHTML = hits
      .map(
        (p) => `<li class="post-item">
  <h2><a href="${esc(p.u)}">${esc(p.t)}</a></h2>
  <p class="post-meta"><time datetime="${esc(p.d)}">${esc(p.d)}</time>${
    p.e ? `<span class="series-chip">${esc(p.e)}</span>` : ''
  }</p>
  <p class="post-summary">${esc(p.s)}</p>
</li>`,
      )
      .join('')
  }

  const run = async (): Promise<void> => {
    const query = searchInput.value.trim().toLowerCase()

    // Queried each time rather than cached: this code does not own the list, and
    // a stale null here silently leaves both lists on screen.
    const postList = document.querySelector<HTMLElement>('#post-list')

    if (query === '') {
      // Hand the page back exactly as it was, including infinite scroll.
      searchResults.hidden = true
      searchCount.textContent = ''
      postList?.removeAttribute('hidden')
      document.querySelector<HTMLElement>('.older-posts')?.removeAttribute('hidden')
      return
    }

    await load()
    const terms = query.split(/\s+/).filter(Boolean)
    // Rank by relevance, then newest first within the same score.
    const hits = (index ?? [])
      .map((p) => ({ p, score: score(p, terms) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score || b.p.d.localeCompare(a.p.d))
      .map((r) => r.p)

    render(hits)
    searchResults.hidden = false
    postList?.setAttribute('hidden', '')
    document.querySelector<HTMLElement>('.older-posts')?.setAttribute('hidden', '')
    searchCount.textContent = `${hits.length} match${hits.length === 1 ? '' : 'es'} for “${searchInput.value.trim()}”`
  }

  let debounce = 0
  searchInput.addEventListener('input', () => {
    window.clearTimeout(debounce)
    debounce = window.setTimeout(run, 120)
  })
  searchInput.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      searchInput.value = ''
      void run()
    }
  })
}
